<template>
  <div style="max-width: 650px">
    <a-spin :spinning="syncFetching">
      <a-form
        :form="form"
        class="start-synchronization-form"
        layout="vertical"
        @submit="handleSubmit"
      >
        <a-form-item
          style="margin-bottom: 8px"
          :label="$t('synchronization.file_type')"
          :colon="false"
        >
          <a-select
            v-decorator="[
              'format',
              {
                initialValue: synchronizationFormats[0].value
              }
            ]"
            :options="synchronizationFormats"
            @change="setXmlNotVerificated"
          ></a-select>
        </a-form-item>
        <a-form-item>
          <div style="display: flex">
            <a-input
              v-decorator="[
                'url',
                {
                  initialValue: ''
                }
              ]"
              :class="{ 'start-synchronization-form--success': !xmlNotVerificated }"
              :placeholder="$t('synchronization.xml_url_label')"
              @change="setXmlNotVerificated"
            />
            <a-button
              style="margin-left: 16px"
              :type="!xmlNotVerificated ? 'primary' : ''"
              :ghost="!xmlNotVerificated"
              :disabled="!hasDeliveries"
              :loading="xmlChecking"
              @click="handleCheckXmlFile"
            >
              {{ $t("synchronization.validation") }}
            </a-button>
          </div>
          <div style="margin-top: 12px">
            <a-alert
              v-if="!xmlNotVerificated"
              :message="$t('synchronization.url_is_valid')"
              type="success"
              show-icon
            />
            <a-alert
              type="error"
              show-icon
              v-if="errors"
            >
              <template #message>
                {{ errors }}
              </template>
            </a-alert>
          </div>
        </a-form-item>
        <div class="start-synchronization-form__settings">
          <div class="start-synchronization-form__settings__title">
            {{ $t("synchronization.synchronization_settings") }}
          </div>
          <a-form-item :colon="false">
            <template #label>
              <span>
                {{ $t("synchronization.upload_products.title") }}
                <a-popover placement="right">
                  <a-icon
                    type="info-circle"
                    theme="twoTone"
                  />
                  <template #content>
                    <div class="start-synchronization-form__popover">
                      {{ $t("synchronization.pop_over.upload") }}
                    </div>
                  </template>
                </a-popover>
              </span>
            </template>
            <a-radio-group
              v-decorator="[
                'load_only',
                {
                  initialValue: uploadProductsOptions[0].value
                }
              ]"
              :disabled="disableForm"
              :options="uploadProductsOptions"
            />
          </a-form-item>
          <a-form-item :colon="false">
            <template #label>
              <span>
                {{ $t("synchronization.update_info_about_product") }}
                <a-popover placement="right">
                  <a-icon
                    type="info-circle"
                    theme="twoTone"
                  />
                  <template #content>
                    <div class="start-synchronization-form__popover">
                      {{ $t("synchronization.pop_over.update") }}
                    </div>
                  </template>
                </a-popover>
              </span>
            </template>
            <a-select
              :disabled="disableForm"
              :options="updateProductOptions"
              v-decorator="[
                'frequency',
                {
                  initialValue: updateProductOptions[0].value
                }
              ]"
            />
          </a-form-item>
          <a-form-item :colon="false">
            <template #label>{{ $t("synchronization.delivery_policy") }}</template>
            <a-select
              :disabled="disableForm"
              :options="deliveryPoliticsOptions"
              :loading="fetchingPolitics"
              v-decorator="[
                'delivery',
                {
                  initialValue: deliveryPoliticsOptions.length
                    ? deliveryPoliticsOptions[0].value
                    : undefined
                }
              ]"
            >
              <template #dropdownRender="menu">
                <VNodes :vnodes="menu" />
                <a-button
                  class="start-synchronization-form__button--select"
                  type="link"
                  @click="handleCreateDelivery()"
                >
                  {{ $t("synchronization.create_new_delivery") }}
                </a-button>
              </template>
            </a-select>
            <a-alert
              type="error"
              show-icon
              v-if="!hasDeliveries && !fetchingPolitics"
            >
              <template #message>
                {{ $t("synchronization.no_policy_message.text") }}
                <a
                  href="#"
                  class="start-synchronization-form__button"
                  type="link"
                  @click.prevent="handleCreateDelivery()"
                >
                  {{ $t("synchronization.no_policy_message.link") }}
                </a>
              </template>
            </a-alert>
          </a-form-item>
          <a-form-item :colon="false">
            <template #label>
              <span>
                {{ $t("synchronization.product_state") }}
                <a-popover placement="right">
                  <a-icon
                    type="info-circle"
                    theme="twoTone"
                  />
                  <template #content>
                    <div class="start-synchronization-form__popover">
                      {{ $t("synchronization.pop_over.state") }}
                    </div>
                  </template>
                </a-popover>
              </span>
            </template>
            <a-select
              :disabled="disableForm"
              v-decorator="[
                'state',
                {
                  initialValue: offerConditionsOptions[0].value
                }
              ]"
            >
              <a-select-option
                v-for="state in offerConditionsOptions"
                :key="`state_${state.value}`"
                :value="state.value"
                :disabled="state.value === 'used-1' || state.value === 'used-2'"
              >
                {{ state.label }}
              </a-select-option>
            </a-select>
          </a-form-item>
          <a-form-item :colon="false">
            <template #label>
              <span>
                {{ $t("synchronization.warranty") }}
                <a-popover placement="right">
                  <a-icon
                    type="info-circle"
                    theme="twoTone"
                  />
                  <template #content>
                    <div class="start-synchronization-form__popover">
                      {{ $t("synchronization.pop_over.warranty") }}
                    </div>
                  </template>
                </a-popover>
              </span>
            </template>
            <a-input-number
              :disabled="disableForm"
              :min="0"
              :max="999"
              :precision="0"
              v-decorator="[
                'warranty',
                {
                  initialValue: 0,
                  rules: [
                    {
                      type: 'number',
                      message: $t('validations.number')
                    }
                  ]
                }
              ]"
              style="width: 125px"
            />
          </a-form-item>
          <a-form-item :colon="false">
            <template #label>
              <span>
                {{ $t("synchronization.editing_prices") }}
                <a-popover placement="right">
                  <a-icon
                    type="info-circle"
                    theme="twoTone"
                  />
                  <template #content>
                    <div class="start-synchronization-form__popover">
                      {{ $t(`synchronization.pop_over.price_edit${editMode ? "_next_sync" : ""}`) }}
                    </div>
                  </template>
                </a-popover>
              </span>
            </template>
            <div class="start-synchronization-form__edit-prices">
              <a-select
                :disabled="disableForm"
                :options="editPricesOptions"
                v-model="editPrices"
                @change="(e) => onEditPriceOptionChange(e)"
              />
              {{ $t("synchronization.on") }}
              <a-input-number
                :disabled="disableForm || !editPrices"
                :min="0"
                :max="maxEditPricePercent"
                :precision="discountPrecision"
                v-decorator="[
                  'discount_percent',
                  {
                    initialValue: 0.0,
                    rules: [
                      {
                        type: 'number',
                        message: $t('validations.number')
                      }
                    ]
                  }
                ]"
                style="width: 80px"
              />
              %
            </div>
          </a-form-item>
        </div>

        <div class="start-synchronization-form__actions">
          <a-form-item>
            <a-button
              :disabled="disableForm || fetching"
              @click="resetForm"
            >
              {{ $t("synchronization.cancel_changes") }}
            </a-button>
          </a-form-item>
          <a-form-item>
            <a-button
              type="primary"
              :disabled="disableForm"
              html-type="submit"
              :loading="fetching"
            >
              {{ $t(`synchronization.${editMode ? "update" : "start"}_synchronization`) }}
            </a-button>
          </a-form-item>
        </div>
      </a-form>
    </a-spin>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia"
import { computed, getCurrentInstance, nextTick, onMounted, ref } from "vue"
import { useRoute, useRouter } from "vue-router/composables"

import { conditions } from "@/consts/offerConditions"
import synchronizationFormats from "@/consts/synchronizationFormats"
import i18n from "@/i18n"
import { useSyncMetaStore } from "@/modules/sync/store/useSyncMetaStore"
import { createSyncPayload, useSyncStore } from "@/modules/sync/store/useSyncStore"
import syncService from "@/services/syncService"
import setFormErrors from "@/utils/setFormErrors"

import { useSyncOptions } from "../useSyncOptions"

// Helper Types/Variables
const VNodes = {
  functional: true,
  render: (h, ctx) => ctx.props.vnodes
}

const props = defineProps({
  editMode: {
    type: Boolean,
    default: false
  }
})

// End Helper Types/Variables

// Store & Route
const syncMetaStore = useSyncMetaStore()
const syncStore = useSyncStore()
const router = useRouter()
const route = useRoute()

const { proxy } = getCurrentInstance()
const { synchronization, fetching: syncFetching } = storeToRefs(syncStore)
const { deliveryPolitics, fetchingPolitics } = storeToRefs(syncMetaStore)
// End Store & Route

// State
const xmlNotVerificated = ref(true)
const fetching = ref(false)
const xmlChecking = ref(false)
const errors = ref("")
const editPrices = ref("")
const discountPrecision = ref(2)

const form = proxy.$form.createForm(proxy)
// End State

// Composables
const { uploadProductsOptions, updateProductOptions, editPricesOptions } = useSyncOptions()
// End Composables

// Computed
const deliveryPoliticsOptions = computed(() => {
  return deliveryPolitics.value.map((item) => ({
    label: item.name,
    value: item.uuid
  }))
})
const hasDeliveries = computed(() => deliveryPolitics.value.length > 0)
const offerConditionsOptions = computed(() => {
  return conditions.map((state) => {
    return {
      label: state.description,
      value: state.backKey + "-" + state.backValue
    }
  })
})
const disableForm = computed(() => {
  return xmlNotVerificated.value || !hasDeliveries.value
})
const maxEditPricePercent = computed(() => {
  return editPrices.value === "ascend" ? 999 : 90
})
// End Computed

// Methods
const resetForm = () => {
  fillForm()
}
const fillForm = () => {
  form.setFields({
    url: {
      value: synchronization.value.url,
      errors: null
    },
    format: {
      value: synchronization.value.format,
      errors: null
    },
    load_only: {
      value: synchronization.value.load_only,
      errors: null
    },
    frequency: {
      value: synchronization.value.frequency,
      errors: null
    },
    delivery: {
      value: synchronization.value.delivery?.uuid,
      errors: null
    },
    state: {
      value: `${synchronization.value.condition_key}-${synchronization.value.condition_value}`,
      errors: null
    },
    warranty: {
      value: synchronization.value.warranty,
      errors: null
    },
    discount_percent: {
      value: synchronization.value.discount_percent,
      errors: null
    }
  })

  if (!synchronization.value.discount_percent) {
    editPrices.value = ""
  } else if (synchronization.value.discount_percent > 0) {
    editPrices.value = "ascend"
  } else {
    editPrices.value = "descend"
  }
}
const setXmlNotVerificated = () => {
  xmlNotVerificated.value = true
}
const handleCheckXmlFile = async () => {
  let data = form.getFieldsValue(["url", "format"])

  // Reset url field errors
  form.setFields({
    url: {
      value: data.url,
      errors: null
    }
  })
  errors.value = ""

  if (data.url !== "") {
    xmlChecking.value = true

    try {
      await syncService.verifySynchronization(data)
      xmlNotVerificated.value = false
    } catch (err) {
      if (err?.response?.data?.url) {
        form.setFields(setFormErrors({ url: err.response.data.url }, data))
        setXmlNotVerificated()
      } else if (err?.response?.data?.xml_created_at) {
        errors.value = Array.isArray(err.response.data.xml_created_at)
          ? err.response.data.xml_created_at.join(", ")
          : err.response.data.xml_created_at
      } else if (err?.response?.data["yml_catalog.date"]) {
        errors.value = Array.isArray(err.response.data["yml_catalog.date"])
          ? err.response.data["yml_catalog.date"].join(", ")
          : err.response.data["yml_catalog.date"]
      } else {
        errors.value =
          i18n.t("synchronization.emptyFieldsError") +
          Object.keys(err?.response?.data || {}).join(", ")
      }
    } finally {
      xmlChecking.value = false
    }
  } else {
    form.setFields(
      setFormErrors({
        url: [i18n.t("validations.required_field")]
      })
    )
  }
}
const createOrUpdateSynchronization = async (values) => {
  fetching.value = true

  const { state, discount_percent = 0, warranty, ...data } = values
  const [backKey, backValue] = state.split("-")
  const payload: createSyncPayload = {
    condition_key: backKey,
    condition_value: backValue ? backValue : 1,
    discount_percent: editPrices.value === "descend" ? -discount_percent : discount_percent,
    warranty: Number(warranty)?.toFixed() || 0,
    ...data
  }

  if (props.editMode) {
    syncService
      .updateSynchronization({
        data: payload,
        uuid: synchronization.value.uuid
      })
      .then(() => {
        xmlNotVerificated.value = true
      })
      .catch((err) => {
        form.setFields(setFormErrors(err.response?.data, form.getFieldsValue()))

        if (err.response?.data.details) {
        }

        throw err
      })
      .finally(() => {
        fetching.value = false
      })
  } else {
    syncService
      .createSynchronization(payload)
      .then(() => {
        router.push({ name: "sync-list" })
        xmlNotVerificated.value = true
      })
      .catch((err) => {
        form.setFields(setFormErrors(err.response?.data, form.getFieldsValue()))
        throw err
      })
      .finally(() => {
        fetching.value = false
      })
  }
}
const handleSubmit = (e) => {
  e.preventDefault()

  form.validateFields((err, values) => {
    if (!err) {
      createOrUpdateSynchronization(values)
    }
  })
}
const onEditPriceOptionChange = (value) => {
  if (!value) {
    form.setFields({ discount_percent: { value: 0, errors: null } })
  }
}
const handleCreateDelivery = () => {
  router.push({
    name: "delivery",
    query: {
      openAddPolicyModal: "true"
    }
  })
}
// End Methods

// Lifecycle
onMounted(() => {
  syncMetaStore.getAllActiveDeliveries()

  // Short delay to ensure form is ready if sync?
  if (props.editMode) {
    syncStore.getSynchronization(route.params.uuid).then(() => {
      fillForm()
      handleCheckXmlFile()
    })
  }
})
// End Lifecycle
</script>

<style lang="scss">
.start-synchronization-form {
  width: 100%;
  display: flex;
  flex-flow: column nowrap;

  &__button {
    padding: 0;

    &--select {
      padding: 11px;
      margin-bottom: 10px;
      text-align: left;
      width: 100%;
    }
  }

  &--success {
    color: $brand-color-6;
  }

  &__settings {
    display: flex;
    flex-flow: column nowrap;

    max-width: 400px;

    &__title {
      font-size: 16px;
      font-weight: 500;
    }

    & > * {
      margin-bottom: 12px;
    }
  }

  &__popover {
    max-width: 375px;
  }

  &__actions {
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-end;
    gap: 16px;
  }

  &__edit-prices {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    gap: 10px;

    & > :first-child {
      width: 155px;
    }
  }
}
</style>
